import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { MapaEstados } from '../components/MapaEstados'
import { useActiveTenant } from '../contexts/tenant'
import { usePermissions } from '../contexts/permissions'

type Invoice = {
  id: string; tenantId: string; periodId: string; status: string
  total: string; generatedAt: string; sentAt: string | null; paidAt: string | null
  periodYear?: number | null; periodMonth?: number | null
}
type InvoiceItem = { id: string; type: string; description: string; quantity: string; unitPrice: string; total: string }
type InvoiceDetail = Invoice & { items: InvoiceItem[] }

const statusVariant: Record<string, 'default' | 'info' | 'warning' | 'success' | 'danger'> = {
  draft: 'default', sent: 'info', paid: 'success', cancelled: 'danger',
}
const itemTypeLabel: Record<string, string> = {
  b2c: 'Envios B2C', b2b: 'Envios B2B', extra_box: 'Caixa extra',
  storage: 'Armazenagem', insurance: 'Seguro', minimum: 'Mínimo mensal', discount: 'Desconto',
  packages: 'Volumes', fulfillment: 'Fulfillment',
  industrializacao: 'Industrialização', extra: 'Cobrança avulsa',
}
const brl = (v: string | number) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const round2 = (n: number) => Math.round(n * 100) / 100
/** Mensagem legível de um erro de chamada — o title do servidor quando existe. */
const msgErro = (e: unknown) => {
  const err = e as { response?: { data?: { title?: string } }; message?: string }
  return err?.response?.data?.title ?? err?.message ?? 'falha na consulta'
}

const today = new Date()

// `billable: false` = palete de produto fabricado pela WS. Conta como ocupação
// do armazém e não gera custo (contrato cl. 2.2). Antes só existia "informar o
// palete", e informar significava cobrar.
type PalletRow = { description: string; quantity: number; billable?: boolean }
// Cobrança avulsa: o que não cabe nas linhas do contrato. `reference` guarda o
// documento (nº do CT-e, NF do frete, pedido de compra) — é o que permite
// conferir a cobrança contra alguma coisa em vez de aceitá-la no escuro.
type ExtraRow = { description: string; quantity: number; unitPrice: number; reference: string }
type SurveyResult = {
  from: string | null; to: string | null; total: number; totalValue: number
  nfCount: number | null; nfExcluded: number | null
  byDay: { date: string; count: number }[]
  byChannel: { channel: string; count: number }[]
  byStatus: { status: string; count: number }[]
}
type ConsRow = { sku: string; name: string; fracionamento: number; reservaCaixas: number; reservaUnidades: number; total: number }
type ReportRow = { servico: string; clausula: string; apuracao: string; qty: string; unit: string; total: number; nota?: string }
type ReportKpi = { label: string; value: string; hint?: string }
type Bucket = { label: string; count: number }
type Analytics = {
  period: { year: number; month: number; from: string; to: string }
  meses: { year: number; month: number; expedidos: number; notas: number; extraBoxes: number }[]
  armazenagem: {
    curva: { date: string; arm: number; frac: number; total: number; source: string }[]
    media: number; pico: number; dias: number
    aging: { ativos: number; d90: number; d180: number; d365: number; semGiro: number; totalSkus: number }
  }
  outbound: { byChannel: Bucket[]; byCarrier: Bucket[]; histLinhas: Bucket[]; histUnidades: Bucket[]; pedidos: number }
}
// Desempenho: a prova do serviço, ao lado da conta. `noPrazoPct` e `acuraciaPct`
// são null quando não houve base para medir — mostrar 0% ali seria acusar a
// operação de uma falha que ninguém mediu.
type Desempenho = {
  period: { year: number; month: number; ref: string }
  /** Consultas que não rodaram. O bloco sai com o resto e o extrato diz o que
   *  faltou — melhor que a seção inteira sumir por causa de uma delas. */
  falhas?: string[]
  referencias: { noPrazoPct: number; acuraciaPct: number; custoSobreFaturamentoPct: [number, number] }
  sla: {
    expedidos: number; comRegistroDeSaida: number; d0: number; d1: number; d2mais: number
    noPrazo: number; noPrazoPct: number | null; horasMedia: number; semRegistro: number
  }
  valor: { pedidos: number; mercadoria: number; pedidosComValor: number }
  inventario: {
    sessoes: number; conferidos: number; batidos: number; divergentes: number; acuraciaPct: number | null
    divergenciasAbertasNoMes: number; divergenciasResolvidasNoMes: number; divergenciasEmAberto: number
  }
  devolucoes: { movimentos: number; unidades: number; pctSobreExpedidos: number | null }
  recebimento: { notas: number; divergentes: number; valor: number; okPct: number | null }
  validade: {
    vencido: number; d30: number; d60: number; d90: number; adiante: number
    itens: { sku: string; nome: string; lote: string; validade: string; unidades: number }[]
  }
  ciclo: { ondas: number; horasTotal: number; horasAbastecer: number; horasImprimir: number; horasBaixar: number }
}
type Mix = {
  produtos: { sku: string; nome: string; unidades: number; pedidos: number }[]
  estados: { uf: string; pedidos: number }[]
  semUf: number
}
type Report = {
  periodo: string; ano: number; mes: number; expedidos: number; rows: ReportRow[]; total: number; emissao: string; vencimento: string
  kpis: ReportKpi[]
  byDay: { date: string; count: number }[]; byChannel: { channel: string; count: number }[]
  estoque: { frac: number; arm: number; total: number; armCaixas: number }
  estoqueItens: { sku: string; name: string; frac: number; arm: number; total: number }[]
  /** Dia do snapshot usado. null = não havia snapshot e a posição é a de hoje —
   *  e o rótulo na tela precisa dizer isso, não chamar de "fechamento". */
  estoqueData: string | null
  franquias: { label: string; contratado: string; consumido: string; ok: boolean }[]
  b2cRate: number
  analytics?: Analytics
  desempenho?: Desempenho
  mix?: Mix
  /** Consultas que falharam ao montar o extrato. Uma seção ausente sem
   *  explicação é lida como "não há dado" — e às vezes é erro. */
  avisos: string[]
}

const MESES = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

/** "2027-03-01" → "01/03/2027" (a validade vem como texto do banco). */
const fmtDia = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '')
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (iso ?? '—')
}

function MiniTable({ rows }: { rows: Bucket[] }) {
  if (!rows.length) return <p className="text-xs text-gray-400">Sem dados.</p>
  return (
    <div className="overflow-hidden rounded-lg border border-gray-100">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-3 py-1.5 text-gray-700">{r.label}</td>
              <td className="px-3 py-1.5 text-right font-semibold text-gray-900">{r.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function BillingPage() {
  const tenantId = useActiveTenant()
  const { can } = usePermissions()
  const canEditBilling = can('billing', 'edit') // clientes (só leitura) não veem Configurações
  const qc = useQueryClient()

  const [tab, setTab] = useState<'relatorio' | 'config'>('relatorio')
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [basis, setBasis] = useState<'nf_emission' | 'shipped'>('nf_emission')

  // Levantamento dos expedidos (NF-e) — sempre por MÊS fechado no Bling
  const [surveyYear, setSurveyYear] = useState(today.getFullYear())
  const [surveyMonth, setSurveyMonth] = useState(today.getMonth() + 1)
  const [surveyBusy, setSurveyBusy] = useState(false)
  const [surveyMsg, setSurveyMsg] = useState<string | null>(null)
  const [survey, setSurvey] = useState<SurveyResult | null>(null)
  const [report, setReport] = useState<Report | null>(null)

  // B2C escalonado (faixa única pelo volume do mês)
  const [b2cP1, setB2cP1] = useState(6.0)
  const [b2cP2, setB2cP2] = useState(5.7)
  const [b2cP3, setB2cP3] = useState(5.13)
  // B2B por faixa de unidades
  const [b2bSmall, setB2bSmall] = useState(20)
  const [b2bLarge, setB2bLarge] = useState(40)
  // Caixa extra
  const [extraBoxPrice, setExtraBoxPrice] = useState(1.25)
  const [extraBoxCount, setExtraBoxCount] = useState(0)
  const [mousseSkus, setMousseSkus] = useState('11,31')
  // Armazenagem
  const [palletPrice, setPalletPrice] = useState(85)
  const [freePallets, setFreePallets] = useState(3)
  const [pallets, setPallets] = useState<PalletRow[]>([])
  // Seguro
  const [insurancePct, setInsurancePct] = useState(0.3) // em %
  const [insuranceBase, setInsuranceBase] = useState(0)
  // Mínimo mensal
  const [minMonthly, setMinMonthly] = useState(9000)
  const [minFrom, setMinFrom] = useState('2026-05-01')
  // Cobranças avulsas: frete que a WS contratou e pagou pelo cliente, material a
  // pedido, serviço pontual. Sem isso o valor só fechava distorcendo outra linha.
  const [extras, setExtras] = useState<ExtraRow[]>([])
  // Estado do botão de gerar o extrato. Existe porque "não reage" e "quebrou"
  // eram a mesma coisa na tela: a função saía calada quando faltava premissa, e
  // cada consulta interna engolia o próprio erro.
  const [gerando, setGerando] = useState(false)
  const [erroRelatorio, setErroRelatorio] = useState<string | null>(null)

  const [detailId, setDetailId] = useState<string | null>(null)

  const { data: invoices = [], isLoading } = useQuery<Invoice[]>({
    queryKey: ['billing', 'invoices', tenantId],
    queryFn: async () => (await api.get('/billing/invoices', { params: tenantId ? { tenantId } : {} })).data,
  })

  // Estoque de fechamento (aproxima o último dia): fracionamento + armazém por SKU.
  const { data: consolidated = [] } = useQuery<ConsRow[]>({
    queryKey: ['stock', 'consolidated', tenantId],
    queryFn: async () => (await api.get('/replenishment/consolidated', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const { data: detail } = useQuery<InvoiceDetail>({
    queryKey: ['billing', 'invoice', detailId],
    queryFn: async () => (await api.get(`/billing/invoices/${detailId}`)).data,
    enabled: !!detailId,
  })

  const [genMsg, setGenMsg] = useState<string | null>(null)
  const generate = useMutation({
    mutationFn: async () => {
      const body = {
        tenantId, year, month, basis,
        b2cTier1Price: b2cP1, b2cTier2Price: b2cP2, b2cTier3Price: b2cP3,
        b2bSmallPrice: b2bSmall, b2bLargePrice: b2bLarge,
        extraBoxPrice, extraBoxCount,
        storagePricePerPallet: palletPrice, freePallets,
        extraPallets: pallets
          .map(p => ({
            description: p.description.trim(),
            quantity: Math.round(p.quantity) || 0,
            // Sem isto, o palete de produção WS ia como cobrável e a isenção da
            // tela não chegava na fatura.
            billable: p.billable !== false,
          }))
          .filter(p => p.description && p.quantity > 0),
        insuranceRate: insurancePct / 100, insuranceBase,
        minimumMonthly: minMonthly, minimumFrom: minFrom,
        extraCharges: extrasValidos.map(e => ({
          description: e.description.trim(),
          quantity: e.quantity,
          unitPrice: e.unitPrice,
          reference: e.reference.trim() || undefined,
        })),
      }
      return (await api.post('/billing/invoices', body)).data as Invoice
    },
    onSuccess: (inv) => {
      setGenMsg(`Rascunho ${month}/${year} gerado/atualizado: ${brl(inv.total)}. Veja abaixo em "faturas".`)
      qc.invalidateQueries({ queryKey: ['billing', 'invoices'] })
      if (inv?.id) setDetailId(inv.id)
    },
  })

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) =>
      api.patch(`/billing/invoices/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing', 'invoices'] }),
  })

  const addPallet = () => setPallets(p => [...p, { description: '', quantity: 1, billable: true }])
  const setPallet = (i: number, patch: Partial<PalletRow>) => setPallets(p => p.map((r, idx) => idx === i ? { ...r, ...patch } : r))
  const delPallet = (i: number) => setPallets(p => p.filter((_, idx) => idx !== i))
  const addExtra = () => setExtras(e => [...e, { description: '', quantity: 1, unitPrice: 0, reference: '' }])
  const setExtra = (i: number, patch: Partial<ExtraRow>) => setExtras(e => e.map((r, idx) => idx === i ? { ...r, ...patch } : r))
  const delExtra = (i: number) => setExtras(e => e.filter((_, idx) => idx !== i))
  const extrasValidos = extras.filter(e => e.description.trim() && e.quantity > 0)
  const extrasTotal = round2(extrasValidos.reduce((s, e) => s + e.quantity * e.unitPrice, 0))

  const qtd = (rs: PalletRow[]) => rs.reduce((s, p) => s + (Math.round(p.quantity) || 0), 0)
  // Palete de produção WS conta como ocupação e não entra no valor: fica fora do
  // total cobrável E fora da franquia de grátis, que existe para abater o que é
  // cobrável.
  const palletsCobraveisQtd = qtd(pallets.filter(p => p.billable !== false))
  const palletsIsentosQtd = qtd(pallets.filter(p => p.billable === false))
  const totalPallets = palletsCobraveisQtd
  const billablePallets = Math.max(0, totalPallets - freePallets)

  const pad2 = (n: number) => String(n).padStart(2, '0')
  const surveyFrom = `${surveyYear}-${pad2(surveyMonth)}-01`
  const surveyTo = `${surveyYear}-${pad2(surveyMonth)}-${pad2(new Date(surveyYear, surveyMonth, 0).getDate())}`

  async function levantar() {
    if (!tenantId) return
    setSurveyBusy(true); setSurvey(null); setSurveyMsg('Consultando o Bling…')
    try {
      const start = (await api.post('/integrations/orders-survey', { tenantId, provider: 'bling', from: surveyFrom, to: surveyTo })).data
      if (start?.started === false) setSurveyMsg(start.reason ?? 'Já há um levantamento em andamento')
      // O levantamento roda em segundo plano e o estado dele vive na memória do
      // servidor. Se o processo reinicia no meio (o que acontecia por estouro de
      // memória), a consulta de status devolve 502 e depois "nenhum
      // levantamento" — e o `catch` de fora transformava tudo em "Falha no
      // levantamento". Aqui as duas situações são separadas e nomeadas.
      let falhasSeguidas = 0
      let jaRodou = false
      for (let i = 0; i < 150; i++) {
        await new Promise(r => setTimeout(r, 2000))
        let st: { running?: boolean; error?: string; result?: SurveyResult; msg?: string }
        try {
          st = (await api.get('/integrations/orders-survey/status', { params: { tenantId } })).data
          falhasSeguidas = 0
        } catch (e) {
          const err = e as { response?: { status?: number } }
          // 502/503 = servidor fora do ar por instantes. Vale insistir; o que não
          // vale é chamar de falha do levantamento na primeira tentativa.
          falhasSeguidas++
          if (falhasSeguidas >= 5) {
            setSurveyMsg(`O servidor parou de responder durante o levantamento (HTTP ${err.response?.status ?? '—'}). Tente um período menor.`)
            return
          }
          setSurveyMsg(`servidor reiniciando (${falhasSeguidas}/5)…`)
          continue
        }
        if (st.running) { jaRodou = true; setSurveyMsg(st.msg ?? 'consultando…'); continue }
        if (st.error) { setSurveyMsg(`Erro: ${st.error}`); return }
        if (st.result) { setSurvey(st.result); setSurveyMsg(null); return }
        // Estava rodando e agora o servidor não conhece mais o levantamento:
        // ele reiniciou e levou o estado junto.
        setSurveyMsg(jaRodou
          ? 'O levantamento foi interrompido — o servidor reiniciou no meio. Tente de novo; se repetir, use um período menor.'
          : (st.msg ?? 'sem resultado'))
        return
      }
      setSurveyMsg('Tempo esgotado — tente um período menor.')
    } catch (e: any) {
      const st = e?.response?.status
      setSurveyMsg(e?.response?.data?.title
        ?? (st ? `Falha no levantamento (HTTP ${st})` : 'Falha no levantamento — sem resposta do servidor'))
    } finally { setSurveyBusy(false) }
  }

  // Monta o extrato mensal completo a partir dos EXPEDIDOS (NF-e) + as premissas
  // das Configurações — descrevendo cada cobrança do contrato (com a cláusula).
  // Todos os expedidos são tratados como B2C (Pele Bonita é marketplace).
  async function gerarRelatorio() {
    // Antes isto era um `return` mudo: sem levantamento, sem cliente ou sem
    // contagem de NF, o botão não fazia nada e não dizia nada — e "não reage" é
    // indistinguível de "quebrou".
    setErroRelatorio(null)
    if (!tenantId) { setErroRelatorio('Selecione um cliente no menu lateral.'); return }
    if (!survey) { setErroRelatorio('Rode o "Levantamento no Bling" antes: o relatório sai dos expedidos do período.'); return }
    if (survey.nfCount == null) {
      setErroRelatorio('O levantamento não trouxe a contagem de NF-e. Rode o levantamento de novo para o período.')
      return
    }
    setGerando(true)
    try {
      await montarRelatorio()
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } }; message?: string }
      setErroRelatorio(err.response?.data?.title ?? err.message ?? 'Falha ao montar o relatório')
    } finally { setGerando(false) }
  }

  async function montarRelatorio() {
    if (!survey || survey.nfCount == null || !tenantId) return
    // Cada consulta interna pode falhar sem derrubar o extrato — mas a seção que
    // ela alimenta some. Sem registrar o motivo, o relatório sai com um buraco e
    // ninguém sabe se o bloco está vazio por falta de dado ou por erro.
    const avisos: string[] = []
    const r2 = (n: number) => Math.round(n * 100) / 100
    const faixa = (t: number) => t === 1 ? '1 a 2.000 envios' : t === 2 ? '2.001 a 5.000 envios' : '5.001+ envios'
    // Período do relatório = o MÊS do LEVANTAMENTO (não o da aba Configurações).
    const repYear = surveyYear
    const repMonth = surveyMonth
    const b2cCount = survey.nfCount
    const tier = b2cCount <= 2000 ? 1 : b2cCount <= 5000 ? 2 : 3
    const b2cRate = tier === 1 ? b2cP1 : tier === 2 ? b2cP2 : b2cP3
    const b2cTotal = r2(b2cCount * b2cRate)

    // Caixa extra: calcula pela regra A/B nos itens dos pedidos do mês (WsFull).
    // Se falhar/ não houver dados, cai na quantidade manual das Configurações.
    let extraBoxes = extraBoxCount
    let extraOrders = 0
    let extraAuto = false
    try {
      const d = (await api.get('/billing/extra-boxes', { params: { tenantId, year: repYear, month: repMonth, basis: 'nf_emission', mousseSkus } })).data
      if (d && typeof d.extraBoxes === 'number') { extraBoxes = d.extraBoxes; extraOrders = d.orders ?? 0; extraAuto = true }
    } catch (e) {
      avisos.push(`caixas extras calculadas: ${msgErro(e)} — usando a quantidade informada à mão`)
    }
    const extraBoxTotal = r2(extraBoxes * extraBoxPrice)

    // Armazenagem: paletes de terceiros no sistema (auto) + paletes manuais.
    let autoPals = 0
    // O motivo importa mais que o número quando ele decepciona: "0 paletes" pode
    // ser não haver palete de terceiro OU ninguém ter marcado produto nenhum, e
    // as duas coisas pedem ações opostas. O erro também não pode mais ser
    // engolido — antes, uma falha na chamada virava "0" silencioso.
    let motivoPals: string | null = null
    // Paletes de produção WS: entram na CONTAGEM (é ocupação real do armazém) e
    // ficam fora do VALOR. Contrato cl. 2.2 — o cliente não paga armazenagem do
    // que a própria WS fabricou.
    let wsPals = 0
    try {
      const d = (await api.get('/billing/storage-pallets', { params: { tenantId } })).data
      if (d && typeof d.thirdPartyPallets === 'number') autoPals = d.thirdPartyPallets
      if (d && typeof d.wsPallets === 'number') wsPals = d.wsPallets
      motivoPals = d?.motivo ?? null
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      motivoPals = `não foi possível contar os paletes de terceiros: ${err.response?.data?.title ?? 'falha na consulta'}`
    }
    const manualPals = palletsCobraveisQtd
    const totalPals = autoPals + manualPals
    // A franquia de grátis abate só o que É cobrável. Gastá-la num palete isento
    // encarecia a fatura sem nada aparecer cobrado a mais.
    const freeUsed = Math.min(freePallets, totalPals)
    const billPals = Math.max(0, totalPals - freeUsed)
    const isentosPals = wsPals + palletsIsentosQtd
    const storageTotal = r2(billPals * palletPrice)
    const insuranceTotal = r2(insuranceBase * insurancePct / 100)
    const minFromD = minFrom ? new Date(`${minFrom}T00:00:00`) : null
    const periodStart = new Date(repYear, repMonth - 1, 1)
    const minApplies = minMonthly > 0 && (!minFromD || periodStart >= minFromD)
    const minComplement = minApplies && b2cTotal < minMonthly ? r2(minMonthly - b2cTotal) : 0

    const rows: ReportRow[] = []
    rows.push({
      servico: 'Envios B2C (cliente final)', clausula: '2.6',
      apuracao: `Tabela escalonada — faixa: ${faixa(tier)}`,
      qty: `${b2cCount} envios`, unit: brl(b2cRate), total: b2cTotal,
    })
    rows.push({
      servico: 'Envios B2B (lojista/atacado)', clausula: '2.5',
      apuracao: '1–50 un R$20 · 51–100 un R$40 · palete R$100',
      qty: '0', unit: '—', total: 0, nota: 'Não apurado neste mês — todos os envios tratados como B2C.',
    })
    rows.push({
      servico: 'Caixa extra (embalagem)', clausula: '2.6-d',
      apuracao: extraAuto
        ? `Regra A/B em ${extraOrders} pedidos (mousses ${mousseSkus} + teto un/5); 1 caixa inclusa/pedido`
        : '1 caixa padrão inclusa por pedido; adicional por caixa (manual)',
      qty: `${extraBoxes} caixas`, unit: brl(extraBoxPrice), total: extraBoxTotal,
      nota: extraBoxes === 0 ? (extraAuto ? 'Nenhum pedido precisou de caixa adicional.' : 'Nenhuma caixa extra informada.') : undefined,
    })
    rows.push({
      servico: 'Armazenagem (produtos de terceiros)', clausula: '2.2',
      apuracao: `${totalPals} palete(s) cobráveis (${autoPals} de terceiros no sistema + ${manualPals} manuais) · ${freeUsed} grátis · ${billPals} cobrado(s)`,
      qty: `${billPals} paletes`, unit: brl(palletPrice), total: storageTotal,
      nota: motivoPals
        ? `${motivoPals}.`
        : totalPals === 0
          ? 'Nenhum palete de terceiros (produtos fabricados pela WS são isentos).'
          : 'Contagem atual — aproxima o pico mensal (sem snapshot histórico).',
    })
    // Linha só de CONTAGEM: os paletes da WS aparecem porque ocupam posição, e
    // valem R$ 0 porque o contrato os isenta. Sem esta linha o cliente conferia a
    // ocupação e não achava o que sabe que está guardado aqui — e a única forma
    // de mostrá-los era lançá-los como cobráveis.
    if (isentosPals > 0) {
      rows.push({
        servico: 'Armazenagem de produção WS (isenta)', clausula: '2.2',
        apuracao: `${isentosPals} palete(s) com produto fabricado pela WS${wsPals ? ` (${wsPals} no sistema${palletsIsentosQtd ? ` + ${palletsIsentosQtd} manuais` : ''})` : ''}`,
        qty: `${isentosPals} paletes`, unit: brl(0), total: 0,
        nota: 'Contados por ocupar posição no armazém; isentos de cobrança — não consomem a franquia de paletes grátis.',
      })
    }
    rows.push({
      servico: 'Seguro de mercadorias de terceiros', clausula: '7.5',
      apuracao: `${insurancePct}% sobre ${brl(insuranceBase)} (valor de entrada — pico do mês)`,
      qty: '—', unit: `${insurancePct}%`, total: insuranceTotal,
      nota: insuranceBase === 0 ? 'Valor armazenado de terceiros não informado.' : undefined,
    })
    if (minComplement > 0) {
      rows.push({
        servico: 'Complemento do mínimo mensal', clausula: '2.11–2.13',
        apuracao: `Piso de ${brl(minMonthly)} (≈1.500 envios B2C) — B2C ficou abaixo`,
        qty: '1', unit: brl(minComplement), total: minComplement,
      })
    }
    // Uma linha POR cobrança avulsa, com a referência do documento. Somá-las num
    // "Diversos" é o que faz o cliente contestar a fatura inteira por causa de um
    // frete de R$ 40 que ele não reconhece.
    for (const e of extrasValidos) {
      rows.push({
        servico: e.description.trim(), clausula: 'avulsa',
        apuracao: e.reference.trim()
          ? `Cobrança avulsa — ref. ${e.reference.trim()}`
          : 'Cobrança avulsa (sem referência informada)',
        qty: `${e.quantity}`, unit: brl(e.unitPrice), total: round2(e.quantity * e.unitPrice),
        nota: e.reference.trim() ? undefined : 'Sem nº de documento — informe a referência para o cliente poder conferir.',
      })
    }

    const total = r2(rows.reduce((s, l) => s + l.total, 0))
    const hoje = new Date()
    const venc = new Date(hoje.getTime() + 5 * 86400000)
    const fmt = (d: Date) => d.toLocaleDateString('pt-BR')

    // ── Estoque de fechamento (aproxima o último dia) ──
    const est = consolidated.reduce((a, r) => {
      a.frac += r.fracionamento; a.arm += r.reservaUnidades; a.armCaixas += r.reservaCaixas; a.total += r.total; return a
    }, { frac: 0, arm: 0, armCaixas: 0, total: 0 })

    // ── KPIs ──
    const diasComEnvio = survey.byDay.filter(d => d.count > 0).length || 1
    const ticket = b2cCount > 0 ? total / b2cCount : 0
    const mediaDia = b2cCount / diasComEnvio
    const pctMin = minMonthly > 0 ? Math.round((b2cTotal / minMonthly) * 100) : 0
    const kpis: ReportKpi[] = [
      { label: 'Expedidos (NF-e)', value: `${b2cCount}` },
      { label: 'Total da fatura', value: brl(total) },
      { label: 'Ticket médio', value: brl(ticket), hint: 'por envio' },
      { label: 'Média diária', value: `${Math.round(mediaDia)}`, hint: `em ${diasComEnvio} dia(s)` },
      { label: 'Faixa B2C', value: `${tier} (${brl(b2cRate)})` },
      { label: '% do mínimo', value: `${pctMin}%`, hint: `piso ${brl(minMonthly)}` },
    ]

    // ── Estoque de FECHAMENTO por SKU ──
    // O relatório dizia "fechamento" e mostrava o estoque de HOJE — em outubro,
    // o extrato de agosto exibia a posição de outubro. Os snapshots diários
    // existem desde sempre; ninguém os estava lendo. Agora busca a posição do
    // último dia do período e só cai no estoque atual quando não há snapshot
    // daquele dia (mês anterior ao início dos snapshots), dizendo qual dos dois
    // está na tela.
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const ultimoDiaMes = new Date(repYear, repMonth, 0)
    const hojeD = new Date()
    // Mês em curso não tem "último dia" ainda: a posição mais recente é a de hoje.
    const alvoFecho = ultimoDiaMes > hojeD ? hojeD : ultimoDiaMes

    const carregarPosicao = async (dia: string) => {
      const pos = (await api.get('/stock/position/day', { params: { tenantId, date: dia } })).data as
        Array<{ sku: string | null; name: string | null; fracUnits: number; armUnits: number; armBoxes: number; totalUnits: number }>
      return Array.isArray(pos) && pos.length ? pos : null
    }

    let estoqueItens = consolidated
      .filter(r => r.total > 0)
      .map(r => ({ sku: r.sku, name: r.name, frac: r.fracionamento, arm: r.reservaUnidades, total: r.total }))
      .sort((a, b) => b.total - a.total)
    let estoqueData: string | null = null
    let est2 = { ...est }
    try {
      // 1º o último dia do período. Se aquele dia não tiver snapshot (falha do
      // agendador, feriado, servidor fora), procura o dia mais recente COM
      // registro DENTRO do período — nunca uma data fora dele. Cair no estoque
      // de hoje seria trocar a posição de agosto pela de outubro sem avisar.
      let pos = await carregarPosicao(iso(alvoFecho))
      let dia = iso(alvoFecho)
      if (!pos) {
        const serie = (await api.get('/stock/position', {
          params: { tenantId, from: `${repYear}-${String(repMonth).padStart(2, '0')}-01`, to: iso(alvoFecho) },
        })).data as Array<{ snapshotDate: string }>
        const ultimo = Array.isArray(serie) && serie.length ? serie[serie.length - 1]?.snapshotDate : null
        if (ultimo) { pos = await carregarPosicao(ultimo); dia = ultimo }
      }
      if (pos) {
        estoqueItens = pos
          .filter(r => (r.totalUnits ?? 0) > 0)
          .map(r => ({ sku: r.sku ?? '—', name: r.name ?? '—', frac: r.fracUnits ?? 0, arm: r.armUnits ?? 0, total: r.totalUnits ?? 0 }))
          .sort((a, b) => b.total - a.total)
        est2 = pos.reduce((a, r) => {
          a.frac += r.fracUnits ?? 0; a.arm += r.armUnits ?? 0
          a.armCaixas += r.armBoxes ?? 0; a.total += r.totalUnits ?? 0
          return a
        }, { frac: 0, arm: 0, armCaixas: 0, total: 0 })
        estoqueData = dia
      }
    } catch (e) {
      avisos.push(`posição de estoque do fechamento: ${msgErro(e)} — a tela mostra a posição atual`)
    }

    // ── Franquias contratadas vs consumidas ──
    const franquias = [
      { label: 'Mínimo mensal (envios B2C)', contratado: '1.500 envios', consumido: `${b2cCount} envios`, ok: b2cCount >= 1500 },
      { label: 'Paletes grátis (armazenagem)', contratado: `${freePallets} paletes`, consumido: `${totalPals} paletes`, ok: totalPals <= freePallets },
      { label: 'Caixa padrão inclusa', contratado: '1 por pedido', consumido: `${extraBoxes} extra(s)`, ok: extraBoxes === 0 },
    ]

    // ── Analytics do backend (aging, outbound, curva de armazenagem) ──
    let analytics: Analytics | undefined
    try {
      analytics = (await api.get('/billing/monthly-analytics', { params: { tenantId, year: repYear, month: repMonth } })).data
    } catch (e) {
      avisos.push(`armazenagem e perfil de pedidos: ${msgErro(e)}`)
    }

    // ── Desempenho: prazo, acurácia, devoluções, validade, ciclo ──
    // É a metade que faltava. Sem ela o extrato é só uma cobrança; com ela o
    // cliente vê o que comprou.
    let desempenho: Desempenho | undefined
    try {
      desempenho = (await api.get('/billing/monthly-performance', { params: { tenantId, year: repYear, month: repMonth } })).data
      for (const f of desempenho?.falhas ?? []) avisos.push(`desempenho — ${f}`)
    } catch (e) {
      avisos.push(`desempenho do mês (prazo, acurácia, validade): ${msgErro(e)}`)
    }

    // ── Para onde foi e o que mais saiu — dados que já existiam no dashboard ──
    let mix: Mix | undefined
    try {
      const de = `${repYear}-${String(repMonth).padStart(2, '0')}-01`
      const fim = new Date(repYear, repMonth, 0)
      const ate = `${repYear}-${String(repMonth).padStart(2, '0')}-${String(fim.getDate()).padStart(2, '0')}`
      const d = (await api.get('/outbound/dashboard/analytics', { params: { tenantId, from: de, to: ate } })).data
      // O mapa precisa de TODOS os estados: cortar no top 10 pintava o resto do
      // Brasil de cinza como se não tivesse tido envio, e ainda calculava a % de
      // cada um sobre a soma dos 10 — inflando a fatia de SP.
      mix = { produtos: (d?.produtos ?? []).slice(0, 10), estados: d?.estados ?? [], semUf: d?.semUf ?? 0 }
    } catch (e) {
      avisos.push(`produtos mais expedidos e mapa por estado: ${msgErro(e)}`)
    }

    setReport({
      periodo: `${MESES[repMonth]}/${repYear}`, ano: repYear, mes: repMonth,
      expedidos: b2cCount,
      rows, total,
      emissao: fmt(hoje), vencimento: fmt(venc),
      kpis,
      byDay: survey.byDay, byChannel: survey.byChannel,
      estoque: { frac: est2.frac, arm: est2.arm, total: est2.total, armCaixas: est2.armCaixas },
      estoqueItens, estoqueData,
      franquias, b2cRate, analytics, desempenho, mix, avisos,
    })
    // O extrato deixou de exibir isto (documento do cliente não é lugar de
    // mensagem técnica), mas jogar fora seria voltar a esconder falha.
    if (avisos.length) console.warn('[extrato] não montou parte do relatório:', avisos)
  }

  /** Imprime SÓ o extrato, em A4.
   *
   *  O CSS precisa saber quais elementos são ancestrais do extrato para não
   *  escondê-los junto com o resto — e é isso que esta função marca antes de
   *  chamar o navegador. Dá para resolver em CSS puro com `:has()`, mas num
   *  navegador sem suporte a regra simplesmente não casa e o aplicativo inteiro
   *  vai para a impressora sem aviso. Marcar em JS é explícito e funciona em
   *  qualquer lugar. */
  function imprimirExtrato() {
    const alvo = document.getElementById('extrato-print')
    if (!alvo) { window.print(); return }
    const marcados: HTMLElement[] = []
    for (let el = alvo.parentElement; el; el = el.parentElement) {
      el.classList.add('print-cadeia')
      marcados.push(el)
    }
    document.body.classList.add('imprimindo')
    const limpar = () => {
      document.body.classList.remove('imprimindo')
      marcados.forEach(el => el.classList.remove('print-cadeia'))
      window.removeEventListener('afterprint', limpar)
    }
    window.addEventListener('afterprint', limpar)
    window.print()
    // Rede de segurança: se o navegador não disparar afterprint (acontece em
    // alguns diálogos de "salvar como PDF"), a tela não pode ficar em branco.
    setTimeout(limpar, 1000)
  }

  // Total da fatura de um mês do comparativo (aplica os preços atuais nos expedidos).
  async function baixarAnexoCsv() {
    if (!report || !tenantId) return
    try {
      const res = await api.get('/billing/audit-csv', { params: { tenantId, year: report.ano, month: report.mes, b2cRate: report.b2cRate }, responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
      const a = document.createElement('a')
      a.href = url; a.download = `anexo-faturamento-${report.ano}-${report.mes}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 15000)
    } catch { /* noop */ }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Faturamento</h2>
        <p className="text-sm text-gray-500">Faturas mensais por cliente — modelo do contrato</p>
      </div>

      {/* Abas */}
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1 w-fit">
        {(canEditBilling ? [['relatorio', 'Relatório'], ['config', 'Configurações']] : [['relatorio', 'Relatório']]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id as 'relatorio' | 'config')}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              tab === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'relatorio' && tenantId && (
        <Card>
          <CardHeader><span className="text-sm font-semibold">Levantamento no Bling — expedidos (NF-e) do mês</span></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Mês</label>
                <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm" value={surveyMonth} onChange={e => setSurveyMonth(parseInt(e.target.value))}>
                  {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <Input label="Ano" type="number" value={surveyYear} onChange={e => setSurveyYear(parseInt(e.target.value) || today.getFullYear())} className="w-24" />
              <Button loading={surveyBusy} onClick={levantar}>Levantar</Button>
              {surveyMsg && <p className="text-xs text-gray-500">{surveyMsg}</p>}
            </div>
            <p className="text-xs text-gray-400">Conta os <b>expedidos</b> (NF-e de saída emitidas no mês inteiro: 01 ao último dia) — pedido sem nota não entra. Quebra por dia e por canal vêm das NF-e. Roda em segundo plano.</p>

            {survey && (
              <div className="space-y-3 pt-1">
                <div className="flex flex-wrap gap-4">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-4 py-3">
                    <p className="text-2xl font-bold text-emerald-700">{survey.nfCount ?? '—'}</p>
                    <p className="text-xs text-gray-500">Expedidos <span className="text-gray-400">(NF-e válidas)</span></p>
                  </div>
                  {survey.nfExcluded != null && survey.nfExcluded > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50/40 px-4 py-3">
                      <p className="text-2xl font-bold text-amber-700">{survey.nfExcluded}</p>
                      <p className="text-xs text-gray-500">NF-e descartadas <span className="text-gray-400">(canceladas/pendentes)</span></p>
                    </div>
                  )}
                  <div className="rounded-lg border border-gray-200 px-4 py-3">
                    <p className="text-2xl font-bold text-gray-400">{survey.total}</p>
                    <p className="text-xs text-gray-500">Pedidos criados <span className="text-gray-400">(referência)</span></p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">
                  <b>Expedidos</b> = NF-e de saída <b>válidas</b> (autorizadas) emitidas no período — pela <b>data de emissão da nota no Bling</b>,
                  que reflete a data real do despacho (imune às importações forçadas). Canceladas/pendentes são descartadas e pedidos sem NF não entram.
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Por canal <span className="text-gray-400">(expedidos)</span></p>
                    <div className="overflow-hidden rounded-lg border border-gray-100">
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-100">
                          {survey.byChannel.map(c => (
                            <tr key={c.channel}>
                              <td className="px-3 py-1.5 text-gray-700">{c.channel}</td>
                              <td className="px-3 py-1.5 text-right font-semibold text-gray-900">{c.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Situação dos pedidos <span className="text-gray-400">(referência)</span></p>
                    <div className="overflow-hidden rounded-lg border border-gray-100">
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-100">
                          {survey.byStatus.map(s => (
                            <tr key={s.status}>
                              <td className="px-3 py-1.5 text-gray-700">{s.status}</td>
                              <td className="px-3 py-1.5 text-right font-semibold text-gray-900">{s.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Por dia <span className="text-gray-400">(expedidos)</span></p>
                    <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-100">
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-100">
                          {survey.byDay.map(d => (
                            <tr key={d.date}>
                              <td className="px-3 py-1.5 text-gray-700">{d.date === 'sem data' ? d.date : new Date(`${d.date}T00:00:00`).toLocaleDateString('pt-BR')}</td>
                              <td className="px-3 py-1.5 text-right font-semibold text-gray-900">{d.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Paletes extras do fechamento — somam à armazenagem de terceiros do mês */}
                {canEditBilling && (
                  <div className="border-t border-gray-100 pt-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Paletes extras (armazenagem do mês)</p>
                    <div className="space-y-2">
                      {pallets.map((p, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input
                            value={p.description}
                            onChange={e => setPallet(i, { description: e.target.value })}
                            placeholder="Descrição (ex.: produto de terceiro X, matéria-prima Y)"
                            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                          />
                          <input
                            type="number" min={1} value={p.quantity}
                            onChange={e => setPallet(i, { quantity: parseInt(e.target.value) || 0 })}
                            className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                          />
                          <span className="text-xs text-gray-400">pal.</span>
                          <select
                            value={p.billable === false ? 'ws' : 'terceiro'}
                            onChange={e => setPallet(i, { billable: e.target.value !== 'ws' })}
                            title="Produção WS conta como ocupação e não gera custo (contrato cl. 2.2)"
                            className="rounded-lg border border-gray-200 px-2 py-2 text-xs"
                          >
                            <option value="terceiro">de terceiro (cobra)</option>
                            <option value="ws">produção WS (isento)</option>
                          </select>
                          <button onClick={() => delPallet(i)} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Remover">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex flex-wrap items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={addPallet}><Plus className="h-4 w-4" /> Adicionar palete</Button>
                        <span className="text-xs text-gray-400">{totalPallets} cobrável(is) · {freePallets} grátis · <b>{billablePallets} cobrado(s)</b> a {brl(palletPrice)}/mês{palletsIsentosQtd ? <> · <b>{palletsIsentosQtd} isento(s)</b> de produção WS (contam, não cobram)</> : null}. Soma aos paletes de terceiros do sistema. <b>Gere o relatório de novo</b> após adicionar.</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Gerador do extrato mensal */}
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <Button onClick={gerarRelatorio} loading={gerando}>Gerar relatório de faturamento</Button>
                    <p className="text-xs text-gray-400">Aplica as regras do contrato sobre os {survey.nfCount ?? 0} expedidos.</p>
                  </div>
                  {erroRelatorio && (
                    <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erroRelatorio}</p>
                  )}
                  {report && !erroRelatorio && !gerando && (
                    <p className="mt-2 text-xs text-emerald-700">
                      Relatório de {report.periodo} pronto — role a página até o extrato, no fim da tela.
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Extrato mensal — descreve todas as cobranças do contrato */}
      {tab === 'relatorio' && report && (
        <Card>
          <CardContent>
            <div id="extrato-print" className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 pb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Extrato de faturamento — {report.periodo}</h3>
                  <p className="text-sm text-gray-500">WS Indústria (operador logístico) → Pele Bonita (cliente) · Contrato de fulfillment</p>
                  <p className="mt-1 text-xs text-gray-400">
                    Período: 01/{String(report.mes).padStart(2, '0')}/{report.ano} 00:00 a {new Date(report.ano, report.mes, 0).getDate()}/{String(report.mes).padStart(2, '0')}/{report.ano} 23:59 (BRT) · corte por emissão da NF-e
                  </p>
                  <p className="text-xs text-gray-400">Emissão {report.emissao} · Vencimento {report.vencimento} (5 dias) · {report.expedidos} envios expedidos · fechamento gerado em {report.emissao}</p>
                </div>
                <div className="flex gap-2 print:hidden">
                  <button onClick={baixarAnexoCsv}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Anexo CSV
                  </button>
                  <button onClick={imprimirExtrato}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Imprimir / PDF (A4)
                  </button>
                </div>
              </div>

              {/* ── Resumo do mês ─────────────────────────────────────────────
                  Quem abre o extrato quer saber em cinco segundos se o mês foi
                  bom. Sem isto a resposta ficava espalhada em oito tabelas, e
                  quem lê rápido lia só o total e fechava. */}
              {/* As falhas de montagem saíram daqui: o extrato vai para o cliente,
                  e mensagem técnica no documento dele não ajuda ninguém. Elas
                  continuam sendo registradas no console do navegador (ver
                  montarRelatorio) — foi assim que apareceu o "operator does not
                  exist: date ~ unknown" que estava derrubando a validade. */}

              {/* Franquias contratadas vs consumidas */}
              <div className="overflow-hidden rounded-lg border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Franquia contratada</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Contratado</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Consumido</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {report.franquias.map((f, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1.5 text-gray-700">{f.label}</td>
                        <td className="px-3 py-1.5 text-right text-gray-600">{f.contratado}</td>
                        <td className={`px-3 py-1.5 text-right font-semibold ${f.ok ? 'text-emerald-700' : 'text-amber-700'}`}>{f.consumido}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                {report.kpis.map((k, i) => (
                  <div key={i} className={`rounded-lg border px-3 py-2.5 ${i === 1 ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200'}`}>
                    <p className={`text-lg font-bold ${i === 1 ? 'text-blue-700' : 'text-gray-900'}`}>{k.value}</p>
                    <p className="text-xs text-gray-500">{k.label}{k.hint && <span className="block text-gray-400">{k.hint}</span>}</p>
                  </div>
                ))}
              </div>

              <p className="secao-titulo pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Composição da cobrança</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-200 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Serviço</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Cláusula</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Apuração</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Qtd</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Unit.</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {report.rows.map((r, i) => (
                      <tr key={i} className={r.total === 0 ? 'text-gray-400' : ''}>
                        <td className="px-3 py-2 font-medium text-gray-800">
                          {r.servico}
                          {r.nota && <span className="block text-xs font-normal text-gray-400">{r.nota}</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{r.clausula}</td>
                        <td className="px-3 py-2 text-gray-500">{r.apuracao}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{r.qty}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{r.unit}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${r.total === 0 ? 'text-gray-400' : 'text-gray-900'}`}>{brl(r.total)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-200 bg-gray-50">
                      <td className="px-3 py-3 text-base font-bold text-gray-900" colSpan={5}>Total do mês</td>
                      <td className="px-3 py-3 text-right text-xl font-bold text-blue-700">{brl(report.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Resumo do mês e Desempenho (prazo, acurácia, custo por pedido)
                  saíram daqui: mediam o banco do WsFull enquanto o resto do
                  extrato mede as NF-e do Bling, e as duas bases não batem. Um
                  indicador que compara universos diferentes não é um indicador
                  ruim — é um número errado com aparência de certo. Voltam quando
                  a importação de notas estiver fechada. */}

              {/* ── Validade: o aviso que só o operador logístico consegue dar ──
                  Só aparece quando há o que avisar. Um bloco de "0 unidades a
                  vencer" todo mês treina o leitor a pular a seção. */}
              {report.desempenho && (report.desempenho.validade.vencido + report.desempenho.validade.d30 + report.desempenho.validade.d60 + report.desempenho.validade.d90) > 0 && (
                <div className="quebra-evitar rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">Atenção — validade do estoque</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-4">
                    {([
                      ['Vencido', report.desempenho.validade.vencido, 'text-red-700'],
                      ['Vence em 30 dias', report.desempenho.validade.d30, 'text-red-600'],
                      ['Em 60 dias', report.desempenho.validade.d60, 'text-amber-700'],
                      ['Em 90 dias', report.desempenho.validade.d90, 'text-amber-600'],
                    ] as const).map(([rot, qtd, cor]) => (
                      <div key={rot}>
                        <p className={`text-lg font-bold ${cor}`}>{qtd.toLocaleString('pt-BR')}</p>
                        <p className="text-xs text-gray-600">{rot}<span className="block text-gray-400">unidades</span></p>
                      </div>
                    ))}
                  </div>
                  {report.desempenho.validade.itens.length > 0 && (
                    <table className="mt-3 w-full text-xs">
                      <thead className="text-amber-800">
                        <tr className="border-b border-amber-200">
                          <th className="px-2 py-1 text-left">SKU</th>
                          <th className="px-2 py-1 text-left">Produto</th>
                          <th className="px-2 py-1 text-left">Lote</th>
                          <th className="px-2 py-1 text-left">Validade</th>
                          <th className="px-2 py-1 text-right">Unidades</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.desempenho.validade.itens.map((i, k) => (
                          <tr key={k} className="border-b border-amber-100">
                            <td className="px-2 py-1 font-mono">{i.sku}</td>
                            <td className="px-2 py-1 text-gray-700">{i.nome}</td>
                            <td className="px-2 py-1 font-mono text-gray-500">{i.lote}</td>
                            <td className="px-2 py-1">{fmtDia(i.validade)}</td>
                            <td className="px-2 py-1 text-right font-semibold">{i.unidades.toLocaleString('pt-BR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <p className="mt-2 text-xs text-amber-800">
                    Lotes com saldo em estoque, ordenados pelo que vence primeiro. Vale programar promoção,
                    devolução ou troca antes que vire perda.
                  </p>
                </div>
              )}

              {/* Evolução por mês ficou de fora do extrato: ela conta as notas do
                  banco do WsFull, e enquanto a importação de NF não fechar esse
                  número fica abaixo do cabeçalho, que vem do Bling. Dois totais
                  para a mesma coisa na mesma página é pior que um só. */}

              {/* Armazenagem — método (média × pico) + aging */}
              {report.analytics?.armazenagem && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Armazenagem — ocupação (un)</p>
                    <div className="flex flex-wrap gap-3">
                      <div className="rounded-lg border border-gray-200 px-3 py-2"><p className="text-lg font-bold text-gray-900">{report.analytics.armazenagem.media}</p><p className="text-xs text-gray-500">Média diária</p></div>
                      <div className="rounded-lg border border-amber-200 bg-amber-50/40 px-3 py-2"><p className="text-lg font-bold text-amber-700">{report.analytics.armazenagem.pico}</p><p className="text-xs text-gray-500">Pico do mês</p></div>
                      <div className="rounded-lg border border-gray-200 px-3 py-2"><p className="text-lg font-bold text-gray-900">{report.analytics.armazenagem.dias}</p><p className="text-xs text-gray-500">Dias com registro</p></div>
                    </div>
                    <p className="mt-2 text-[11px] text-gray-400">Critério: média diária de ocupação (mais defensável) — pico exibido para referência.</p>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Aging de estoque <span className="text-gray-400">(SKUs sem giro)</span></p>
                    <div className="overflow-hidden rounded-lg border border-gray-100">
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-100">
                          {(() => { const a = report.analytics.armazenagem.aging; const tot = a.totalSkus || 1; const p = (n: number) => Math.round((n / tot) * 100)
                            return [
                              ['Com giro (≤90d)', a.ativos, 'text-emerald-700'],
                              ['Sem giro +90d', a.d90, 'text-gray-700'],
                              ['Sem giro +180d', a.d180, 'text-amber-700'],
                              ['Sem giro +365d', a.d365, 'text-red-600'],
                            ].map(([label, n, cls], i) => (
                              <tr key={i}><td className="px-3 py-1.5 text-gray-700">{label as string}</td><td className={`px-3 py-1.5 text-right font-semibold ${cls}`}>{n as number}</td><td className="px-3 py-1.5 text-right text-xs text-gray-400">{p(n as number)}%</td></tr>
                            )) })()}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-400">{report.analytics.armazenagem.aging.totalSkus} SKU(s) com estoque. Base p/ cobrança de longa permanência.</p>
                  </div>
                </div>
              )}

              {/* Outbound — canal, transportadora, histogramas */}
              {report.analytics?.outbound && (
                <div className="grid gap-4 lg:grid-cols-4">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Por canal</p>
                    <MiniTable rows={report.analytics.outbound.byChannel} />
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Por transportadora</p>
                    <MiniTable rows={report.analytics.outbound.byCarrier} />
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Linhas / pedido</p>
                    <MiniTable rows={report.analytics.outbound.histLinhas} />
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Unidades / pedido</p>
                    <MiniTable rows={report.analytics.outbound.histUnidades} />
                  </div>
                </div>
              )}

              {/* ── O que mais saiu e para onde ────────────────────────────────
                  Já existia no dashboard interno e nunca chegava ao cliente. É a
                  informação que ele usa para decidir compra e estoque — e que
                  ninguém além de quem separa as caixas consegue dar. */}
              {report.mix && (report.mix.produtos.length > 0 || report.mix.estados.length > 0) && (
                <div className="grid gap-4 lg:grid-cols-2">
                  {report.mix.produtos.length > 0 && (
                    <div>
                      <p className="secao-titulo mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Produtos mais expedidos</p>
                      <table className="w-full text-xs">
                        <thead className="text-gray-500">
                          <tr className="border-b border-gray-200">
                            <th className="px-2 py-1 text-left">SKU</th>
                            <th className="px-2 py-1 text-left">Produto</th>
                            <th className="px-2 py-1 text-right">Pedidos</th>
                            <th className="px-2 py-1 text-right">Unidades</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.mix.produtos.map((p, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              <td className="px-2 py-1 font-mono">{p.sku}</td>
                              <td className="px-2 py-1 text-gray-700">{p.nome}</td>
                              <td className="px-2 py-1 text-right text-gray-600">{p.pedidos.toLocaleString('pt-BR')}</td>
                              <td className="px-2 py-1 text-right font-semibold text-gray-900">{p.unidades.toLocaleString('pt-BR')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {report.mix.estados.length > 0 && (
                    <div className="quebra-evitar mapa-print">
                      <p className="secao-titulo mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Destino por estado</p>
                      {/* Mesmo mapa do dashboard, sem hover: no papel não existe
                          mouse, e o rótulo que só aparece ao passar por cima
                          simplesmente não sai impresso. */}
                      <MapaEstados estados={report.mix.estados} interativo={false} semUf={report.mix.semUf} />
                      {report.mix.semUf > 0 && (
                        <p className="mt-1 text-[11px] text-gray-400">
                          {report.mix.semUf} pedido(s) sem UF no endereço — importados só pela NF, sem dados de entrega.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Análise: curva por dia + estoque de fechamento + por canal */}
              <div className="grid gap-4 lg:grid-cols-3">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Curva de expedição (por dia)</p>
                  <div className="space-y-1">
                    {(() => {
                      const max = Math.max(1, ...report.byDay.map(d => d.count))
                      return report.byDay.map(d => (
                        <div key={d.date} className="flex items-center gap-2">
                          <span className="w-16 shrink-0 text-xs text-gray-500">{d.date === 'sem data' ? '—' : new Date(`${d.date}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                          <div className="h-3 flex-1 rounded bg-gray-100">
                            <div className="h-3 rounded bg-blue-500" style={{ width: `${Math.round((d.count / max) * 100)}%` }} />
                          </div>
                          <span className="w-8 shrink-0 text-right text-xs font-semibold text-gray-700">{d.count}</span>
                        </div>
                      ))
                    })()}
                    {report.byDay.length === 0 && <p className="text-xs text-gray-400">Sem dados de expedição.</p>}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Estoque de fechamento{' '}
                    <span className="text-gray-400">({report.estoqueData ? fmtDia(report.estoqueData) : 'posição atual'})</span>
                  </p>
                  <div className="space-y-2">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-3 py-2">
                      <p className="text-lg font-bold text-emerald-700">{report.estoque.frac} <span className="text-xs text-gray-400">un</span></p>
                      <p className="text-xs text-gray-500">Fracionamento (vendável)</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 px-3 py-2">
                      <p className="text-lg font-bold text-gray-900">{report.estoque.arm} <span className="text-xs text-gray-400">un · {report.estoque.armCaixas} cx</span></p>
                      <p className="text-xs text-gray-500">Armazém / pulmão</p>
                    </div>
                    <div className="rounded-lg border border-blue-200 bg-blue-50/40 px-3 py-2">
                      <p className="text-lg font-bold text-blue-700">{report.estoque.total} <span className="text-xs text-blue-300">un</span></p>
                      <p className="text-xs text-gray-500">Total em estoque</p>
                    </div>
                    <p className="text-[11px] text-gray-400">
                      {report.estoqueData
                        ? `Posição registrada em ${fmtDia(report.estoqueData)} — snapshot do próprio dia, dentro do período, não o estoque de hoje.`
                        : 'Sem snapshot em todo o período: esta é a posição ATUAL, não a do fechamento.'}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Expedidos por canal</p>
                  <div className="overflow-hidden rounded-lg border border-gray-100">
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-gray-100">
                        {report.byChannel.map(c => (
                          <tr key={c.channel}>
                            <td className="px-3 py-1.5 text-gray-700">{c.channel}</td>
                            <td className="px-3 py-1.5 text-right font-semibold text-gray-900">{c.count}</td>
                          </tr>
                        ))}
                        {report.byChannel.length === 0 && <tr><td className="px-3 py-2 text-xs text-gray-400">Sem dados.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Estoque de fechamento por SKU */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Estoque de fechamento por SKU <span className="text-gray-400">(atual)</span></p>
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="w-full text-sm">
                    <thead className="border-b border-gray-100 bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">SKU</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Produto</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Fracionamento</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Armazém</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {report.estoqueItens.map(it => (
                        <tr key={it.sku}>
                          <td className="px-3 py-1.5 font-mono text-xs text-gray-500">{it.sku}</td>
                          <td className="px-3 py-1.5 text-gray-700">{it.name}</td>
                          <td className="px-3 py-1.5 text-right text-emerald-700">{it.frac}</td>
                          <td className="px-3 py-1.5 text-right text-gray-600">{it.arm}</td>
                          <td className="px-3 py-1.5 text-right font-semibold text-gray-900">{it.total}</td>
                        </tr>
                      ))}
                      {report.estoqueItens.length === 0 && <tr><td colSpan={5} className="px-3 py-2 text-xs text-gray-400">Sem estoque no fechamento.</td></tr>}
                    </tbody>
                    {report.estoqueItens.length > 0 && (
                      <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                        <tr>
                          <td className="px-3 py-2 font-semibold text-gray-900" colSpan={2}>Total ({report.estoqueItens.length} SKU)</td>
                          <td className="px-3 py-2 text-right font-semibold text-emerald-700">{report.estoque.frac}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-700">{report.estoque.arm}</td>
                          <td className="px-3 py-2 text-right font-bold text-blue-700">{report.estoque.total}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-500">
                <p><b>Condições (contrato Pele Bonita):</b></p>
                <p>· Vencimento em 5 dias após a emissão; atraso: multa 2% + juros 1%/mês + atualização monetária (cl. 2.18).</p>
                <p>· Mínimo mensal de {brl(minMonthly)} (≈1.500 envios B2C) a partir de 01/05/2026 (cl. 2.11–2.16).</p>
                <p>· Armazenagem: R$85/palete/mês, 3 grátis, pico mensal — só produtos de terceiros; WS-fabricados isentos (cl. 2.2).</p>
                <p>· Seguro 0,3% sobre o valor de entrada das mercadorias de terceiros, pico do mês (cl. 7.5). Reajuste anual IGP-M (cl. 2.17). Setup e inbound isentos.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'config' && canEditBilling && tenantId && (
        <Card>
          <CardHeader><span className="text-sm font-semibold">Gerar fatura</span></CardHeader>
          <CardContent className="space-y-5">
            {/* Período */}
            <div className="flex flex-wrap items-end gap-3">
              <Input label="Ano" type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} className="w-24" />
              <Input label="Mês" type="number" min={1} max={12} value={month} onChange={e => setMonth(parseInt(e.target.value))} className="w-20" />
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Apurar por</label>
                <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm" value={basis} onChange={e => setBasis(e.target.value as 'nf_emission' | 'shipped')}>
                  <option value="nf_emission">Emissão da NF</option>
                  <option value="shipped">Data de expedição</option>
                </select>
              </div>
            </div>
            <p className="-mt-2 text-xs text-gray-400">
              {basis === 'nf_emission'
                ? 'Conta pedidos com NF emitida no mês (recomendado — mais confiável após uploads em massa).'
                : 'Conta pedidos com data de expedição no mês (shippedAt).'}
            </p>

            {/* Envios B2C */}
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Envios B2C (cliente final)</p>
              <div className="flex flex-wrap items-end gap-3">
                <Input label="1–2.000 (R$)" type="number" value={b2cP1} onChange={e => setB2cP1(parseFloat(e.target.value) || 0)} className="w-28" />
                <Input label="2.001–5.000 (R$)" type="number" value={b2cP2} onChange={e => setB2cP2(parseFloat(e.target.value) || 0)} className="w-32" />
                <Input label="5.001+ (R$)" type="number" value={b2cP3} onChange={e => setB2cP3(parseFloat(e.target.value) || 0)} className="w-28" />
              </div>
              <p className="mt-2 text-xs text-gray-400">Marketplaces (Bling/Olist). Faixa única: o volume total do mês define o preço de todos os envios.</p>
            </div>

            {/* Envios B2B */}
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Envios B2B (lojista / atacado)</p>
              <div className="flex flex-wrap items-end gap-3">
                <Input label="1–50 un (R$)" type="number" value={b2bSmall} onChange={e => setB2bSmall(parseFloat(e.target.value) || 0)} className="w-28" />
                <Input label="51–100 un (R$)" type="number" value={b2bLarge} onChange={e => setB2bLarge(parseFloat(e.target.value) || 0)} className="w-28" />
              </div>
              <p className="mt-2 text-xs text-gray-400">Pedidos do canal manual. A faixa é definida pelas unidades de cada pedido.</p>
            </div>

            {/* Caixa extra */}
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Caixa extra</p>
              <div className="flex flex-wrap items-end gap-3">
                <Input label="Preço por caixa (R$)" type="number" value={extraBoxPrice} onChange={e => setExtraBoxPrice(parseFloat(e.target.value) || 0)} className="w-40" />
                <Input label="SKUs de mousse (regra A)" value={mousseSkus} onChange={e => setMousseSkus(e.target.value)} placeholder="11,31" className="w-44" />
                <Input label="Qtd manual (fallback)" type="number" min={0} value={extraBoxCount} onChange={e => setExtraBoxCount(parseInt(e.target.value) || 0)} className="w-40" />
              </div>
              <p className="mt-2 text-xs text-gray-400">No <b>Relatório</b>, as caixas extras são <b>calculadas</b> pela regra A/B (mousses ocupam 2 caixas; volume = teto de unidades/5; máx das duas) sobre os itens de cada pedido — 1 caixa é inclusa por pedido. A "qtd manual" só é usada se o cálculo falhar.</p>
            </div>

            {/* Armazenagem */}
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Armazenagem (produtos de terceiros)</p>
              <div className="flex flex-wrap items-end gap-3">
                <Input label="R$ por palete/mês" type="number" value={palletPrice} onChange={e => setPalletPrice(parseFloat(e.target.value) || 0)} className="w-36" />
                <Input label="Paletes grátis" type="number" min={0} value={freePallets} onChange={e => setFreePallets(parseInt(e.target.value) || 0)} className="w-28" />
              </div>
              <div className="mt-3 space-y-2">
                {pallets.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={p.description}
                      onChange={e => setPallet(i, { description: e.target.value })}
                      placeholder="Descrição do palete (ex.: matéria-prima X, produto terceiro Y)"
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                    <input
                      type="number" min={1} value={p.quantity}
                      onChange={e => setPallet(i, { quantity: parseInt(e.target.value) || 0 })}
                      className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                    <span className="text-xs text-gray-400">pal.</span>
                    <select
                      value={p.billable === false ? 'ws' : 'terceiro'}
                      onChange={e => setPallet(i, { billable: e.target.value !== 'ws' })}
                      title="Produção WS conta como ocupação e não gera custo (contrato cl. 2.2)"
                      className="rounded-lg border border-gray-200 px-2 py-2 text-xs"
                    >
                      <option value="terceiro">de terceiro (cobra)</option>
                      <option value="ws">produção WS (isento)</option>
                    </select>
                    <button onClick={() => delPallet(i)} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Remover">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={addPallet}><Plus className="h-4 w-4" /> Adicionar palete</Button>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                {totalPallets} palete(s) cobrável(is) · {freePallets} grátis · <b>{billablePallets} cobrado(s)</b> a {brl(palletPrice)}/mês = {brl(billablePallets * palletPrice)}.
                {palletsIsentosQtd > 0 && <> {palletsIsentosQtd} palete(s) de produção WS entram na fatura com valor zero — contam a ocupação, não geram custo nem consomem os grátis.</>}
                {' '}Produtos fabricados pela WS são isentos.
              </p>
            </div>

            {/* Seguro */}
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Seguro de mercadorias de terceiros</p>
              <div className="flex flex-wrap items-end gap-3">
                <Input label="Valor armazenado (R$)" type="number" value={insuranceBase} onChange={e => setInsuranceBase(parseFloat(e.target.value) || 0)} className="w-44" />
                <Input label="Taxa (%)" type="number" value={insurancePct} onChange={e => setInsurancePct(parseFloat(e.target.value) || 0)} className="w-24" />
              </div>
              <p className="mt-2 text-xs text-gray-400">
                {brl(insuranceBase)} × {insurancePct}% = {brl(insuranceBase * insurancePct / 100)}. Base = valor de entrada (NF) das mercadorias de terceiros — pico do mês.
              </p>
            </div>

            {/* Cobranças avulsas — o que não cabe nas linhas do contrato */}
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Cobranças avulsas</p>
              <p className="mb-2 text-xs text-gray-400">
                Frete que a WS contratou e pagou pelo cliente, material comprado a pedido, serviço pontual.
                Cada uma vira uma linha própria na fatura — informe a referência do documento para o cliente poder conferir.
              </p>
              <div className="space-y-2">
                {extras.map((e, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <input
                      value={e.description}
                      onChange={ev => setExtra(i, { description: ev.target.value })}
                      placeholder="Descrição (ex.: Frete Jadlog — coleta 12/09)"
                      className="min-w-48 flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                    <input
                      value={e.reference}
                      onChange={ev => setExtra(i, { reference: ev.target.value })}
                      placeholder="Ref. (CT-e, NF, pedido)"
                      className="w-44 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                    <input
                      type="number" min={0} step="0.01" value={e.quantity}
                      onChange={ev => setExtra(i, { quantity: parseFloat(ev.target.value) || 0 })}
                      title="Quantidade"
                      className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                    <span className="text-xs text-gray-400">×</span>
                    <input
                      type="number" step="0.01" value={e.unitPrice}
                      onChange={ev => setExtra(i, { unitPrice: parseFloat(ev.target.value) || 0 })}
                      title="Valor unitário (negativo = abatimento)"
                      className="w-28 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                    <span className="w-24 text-right text-sm font-medium text-gray-700">{brl(round2(e.quantity * e.unitPrice))}</span>
                    <button onClick={() => delExtra(i)} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Remover">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={addExtra}><Plus className="h-4 w-4" /> Adicionar cobrança</Button>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                {extrasValidos.length} cobrança(s) · <b>{brl(extrasTotal)}</b>.
                {' '}Valor unitário negativo vira abatimento. <b>Gere o relatório de novo</b> após alterar.
              </p>
            </div>

            {/* Mínimo mensal */}
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Mínimo mensal (piso do B2C)</p>
              <div className="flex flex-wrap items-end gap-3">
                <Input label="Piso (R$)" type="number" value={minMonthly} onChange={e => setMinMonthly(parseFloat(e.target.value) || 0)} className="w-32" />
                <Input label="Vigora a partir de" type="date" value={minFrom} onChange={e => setMinFrom(e.target.value)} className="w-44" />
              </div>
              <p className="mt-2 text-xs text-gray-400">Se o faturável B2C ficar abaixo do piso, cobra a diferença. Use 0 para desligar.</p>
            </div>

            <div className="flex items-center gap-3">
              <Button loading={generate.isPending} onClick={() => { setGenMsg(null); generate.mutate() }}>Gerar fatura</Button>
              <p className="text-xs text-gray-400">Gera ou regenera o rascunho do período. Use 0 para desligar uma linha.</p>
            </div>
            {genMsg && <p className="text-sm font-medium text-emerald-700">{genMsg}</p>}
            {generate.error && (
              <p className="text-sm text-red-600">{(generate.error as any)?.response?.data?.detail ?? (generate.error as any)?.response?.data?.title ?? (generate.error as any)?.response?.data?.message ?? 'Erro ao gerar'}</p>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'config' && canEditBilling && (
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : invoices.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhuma fatura gerada</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Período</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Gerada em</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Total</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Pago em</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium text-gray-900">{inv.periodMonth ? `${MESES[inv.periodMonth]}/${inv.periodYear}` : '—'}</td>
                      <td className="px-5 py-3 text-gray-700">{new Date(inv.generatedAt).toLocaleDateString('pt-BR')}</td>
                      <td className="px-5 py-3 text-right font-semibold text-gray-900">{brl(inv.total)}</td>
                      <td className="px-5 py-3"><Badge variant={statusVariant[inv.status] ?? 'default'}>{inv.status}</Badge></td>
                      <td className="px-5 py-3 text-gray-500">{inv.paidAt ? new Date(inv.paidAt).toLocaleDateString('pt-BR') : '—'}</td>
                      <td className="px-5 py-3">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setDetailId(inv.id)}>Ver</Button>
                          {inv.status === 'draft' && (
                            <Button variant="secondary" size="sm" loading={updateStatus.isPending} onClick={() => updateStatus.mutate({ id: inv.id, status: 'sent' })}>Enviar</Button>
                          )}
                          {inv.status === 'sent' && (
                            <Button size="sm" loading={updateStatus.isPending} onClick={() => updateStatus.mutate({ id: inv.id, status: 'paid' })}>Marcar pago</Button>
                          )}
                          {(inv.status === 'draft' || inv.status === 'sent') && (
                            <Button variant="ghost" size="sm" loading={updateStatus.isPending}
                              onClick={() => { if (window.confirm('Cancelar esta fatura? Ela deixa de valer e some da apuração do período.')) updateStatus.mutate({ id: inv.id, status: 'cancelled' }) }}>
                              Cancelar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Modal detalhe da fatura */}
      <Modal title="Detalhe da fatura" open={!!detailId} onClose={() => setDetailId(null)} size="lg">
        {!detail ? (
          <p className="py-6 text-center text-sm text-gray-400">Carregando...</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <Badge variant={statusVariant[detail.status] ?? 'default'}>{detail.status}</Badge>
              <span className="text-lg font-bold text-gray-900">{brl(detail.total)}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Tipo</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Descrição</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Qtd</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Unit.</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.items.length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400">Sem itens nesta fatura</td></tr>
                  ) : detail.items.map(it => (
                    <tr key={it.id}>
                      <td className="px-3 py-2"><Badge variant="default">{itemTypeLabel[it.type] ?? it.type}</Badge></td>
                      <td className="px-3 py-2 text-gray-700">{it.description}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{Number(it.quantity)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{brl(it.unitPrice)}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${Number(it.total) < 0 ? 'text-emerald-600' : ''}`}>{brl(it.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
